// CapitalFitBanner.jsx — DAS-2364
//
// Aggregate capital-fit state shown at the top of the Alerts modal:
//   "Capital status: DeGiro €33.2k · Saxo €1.7k · slot €3k · ⚙"
//
// Reads per-part cash from /api/get_capital_fit_state (backend uses
// DG_Portfolio_Topline.cash_eur_value WHERE account IS NULL + Saxo_Topline.Saxo_cash_eur).
// Settings popover lives here too — target position size + preferred part
// for new tickers, both persisted to localStorage (DAS-2356 UX pattern).
//
// The settings only affect DISPLAY. The backend snapshot at alert-fire
// time uses its own env-var values (DR_CAPITAL_FIT_TARGET_EUR / DR_CAPITAL_FIT_DEFAULT_PART).
// If display and snapshot disagree, the user sees the backend numbers in
// each alert's `capital_fit_reason` string.
//
// Design history: DAS-2363 (discussion, Question E1=1+2+3). Implementation: DAS-2364.

import React, { useEffect, useState } from 'react';
import { Popover, InputNumber, Select, Button, Typography } from 'antd';
import { SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  fetchCapitalFitState,
  readCapitalFitSettings,
  writeCapitalFitSettings,
  DAS2364_DEFAULT_TARGET_EUR,
  DAS2364_DEFAULT_PART,
} from '../functions/alertClauseService';

const { Text } = Typography;

const fmtK = (v) => (v === null || v === undefined) ? '?' : `€${(v / 1000).toFixed(1)}k`;

const CapitalFitBanner = () => {
  const [state, setState] = useState(null);
  const initial = readCapitalFitSettings();
  const [targetEur, setTargetEur] = useState(initial.targetEur);
  const [preferredPart, setPreferredPart] = useState(initial.preferredPart);
  const [draftTarget, setDraftTarget] = useState(initial.targetEur);
  const [draftPart, setDraftPart] = useState(initial.preferredPart);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fetch the aggregate per-part state once on mount. The endpoint is cheap
  // (two SELECTs against latest Topline rows) so no need to re-poll.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchCapitalFitState();
      if (!cancelled) setState(data || null);
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply commits the draft pair to state + localStorage.
  const handleApply = () => {
    writeCapitalFitSettings({ targetEur: draftTarget, preferredPart: draftPart });
    setTargetEur(draftTarget);
    setPreferredPart(draftPart);
    setSettingsOpen(false);
  };

  const handleReset = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    writeCapitalFitSettings({ targetEur: null, preferredPart: null });
    setTargetEur(DAS2364_DEFAULT_TARGET_EUR);
    setPreferredPart(DAS2364_DEFAULT_PART);
    setDraftTarget(DAS2364_DEFAULT_TARGET_EUR);
    setDraftPart(DAS2364_DEFAULT_PART);
    setSettingsOpen(false);
  };

  const settingsContent = (
    <div style={{ width: 280 }}>
      <div style={{ marginBottom: 8, fontSize: 12, color: '#555' }}>
        Display-only settings — the backend snapshot at alert-fire time uses
        its own env vars (DAS-2364). These control the slot size and default
        part shown in the banner / popover for triage.
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Target position size (€100 – €100,000)</div>
        <InputNumber
          value={draftTarget}
          onChange={(v) => setDraftTarget(v)}
          min={100} max={100000} step={500} style={{ width: '100%' }}
          formatter={(v) => `€ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(v) => v.replace(/[€\s,]/g, '')}
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Preferred part for new tickers</div>
        <Select
          value={draftPart}
          onChange={(v) => setDraftPart(v)}
          style={{ width: '100%' }}
          options={[
            { value: 'degiro', label: 'DeGiro (primary, not rebalance)' },
            { value: 'saxo',   label: 'Saxo' },
          ]}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <a href="#" style={{ fontSize: 11 }} onClick={handleReset}>Reset to defaults</a>
        <Button
          type="primary"
          size="small"
          disabled={
            draftTarget === null ||
            !(draftTarget >= 100 && draftTarget <= 100000) ||
            !(draftPart === 'degiro' || draftPart === 'saxo')
          }
          onClick={handleApply}
        >Apply</Button>
      </div>
    </div>
  );

  if (!state) {
    return null;   // Soft-fail: backend unavailable → hide the banner
  }

  const saxoCash   = state.saxo?.cash_eur;
  const degiroCash = state.degiro_primary?.cash_eur;
  // Show banner only if we have at least one of the two parts' cash. Otherwise
  // there's nothing useful to render.
  if (saxoCash === null && degiroCash === null) return null;

  return (
    <div style={{
      marginBottom: 12, padding: '6px 12px',
      background: '#f0f5ff', border: '1px solid #d6e4ff', borderRadius: 4,
      fontSize: 12, display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <Text strong style={{ fontSize: 12 }}>💰 Capital status:</Text>
      <Text>DeGiro {fmtK(degiroCash)}</Text>
      <Text type="secondary">·</Text>
      <Text>Saxo {fmtK(saxoCash)}</Text>
      <Text type="secondary">·</Text>
      <Text>slot {fmtK(targetEur)}</Text>
      <Text type="secondary">·</Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        new tickers → {preferredPart === 'degiro' ? 'DeGiro' : 'Saxo'}
      </Text>
      <span style={{ flex: 1 }} />
      <Popover
        open={settingsOpen}
        onOpenChange={(open) => {
          if (open) {
            setDraftTarget(targetEur);
            setDraftPart(preferredPart);
          }
          setSettingsOpen(open);
        }}
        content={settingsContent}
        title="Capital-fit display settings (DAS-2364)"
        trigger="click"
        placement="bottomRight"
      >
        <SettingOutlined
          title="Adjust display settings"
          style={{ color: '#1890ff', cursor: 'pointer' }}
        />
      </Popover>
      <InfoCircleOutlined
        style={{ color: '#999', fontSize: 11, cursor: 'help' }}
        title={
          "Per-part cash check applied to every Accumulate / Step-In alert at " +
          "fire time. saxo and degiro cash are tracked; manual + WenO are " +
          "treated as 'N/A' (no per-part cash table). Settings here are display-only; " +
          "the backend snapshot uses its own env vars."
        }
      />
    </div>
  );
};

export default CapitalFitBanner;
